import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import './WordNetwork.scss';

const useStyles = makeStyles(theme => ({
  wrapper: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& > div': {
      width: 700,
      maxWidth: '100%',
      background: '#fff',
      padding: 20,
      maxHeight: '100%',
      overflow: 'auto',
      border: '2px solid #979bea',
      borderRadius: '2em',
    },
    '& h3': {
      textAlign: 'center',
      color: theme.palette.primary.main,
      marginTop: 0,
      cursor: 'pointer',
    },
  },
  help: {
    textAlign: 'center',
    color: '#c35e0d',
    marginTop: 0,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th': {
      width: '33.33%',
      '& div': {
        padding: '0.5em',
        cursor: 'pointer',
        borderRadius: '2em',
      },
      '&:nth-child(1) div': {
        backgroundColor: '#e4a4d6',
      },
      '&:nth-child(2) div': {
        backgroundColor: '#979bea',
      },
      '&:nth-child(3) div': {
        backgroundColor: '#ffbb85',
      },
    },
    '& td': {
      width: '33.33%',
      textAlign: 'center',
      cursor: 'pointer',
      '& div': {
        width: '100%',
        minHeight: '1.75em',
        fontWeight: 500,
        boxSizing: 'border-box',
        borderRadius: '1.5em',
        transition: 'all 0.1s',
      },
      '&:nth-child(1) div': {
        backgroundColor: '#ffecfb',
        border: '2px solid #e4a4d6',
      },
      '&:nth-child(2) div': {
        backgroundColor: '#dadcff',
        border: '2px solid #979bea',
      },
      '&:nth-child(3) div': {
        backgroundColor: '#ffead9',
        border: '2px solid #ffbb85',
      },
      '& button': {
        marginBottom: 0,
      },
    },
    '& button': {
      width: '95%',
      margin: '5px 0 5px',
    },
  },
  availWords: {
    width: '100%',
    textAlign: 'center',
    marginBottom: 10,
    minHeight: 43,
    '& span': {
      display: 'inline-block',
      padding: '0.25em 1em',
      background: '#a6da08',
      borderRadius: '1.5em',
      margin: 5,
      transition: 'transform 0.1s',
      cursor: 'pointer',
      userSelect: 'none',
      '&:hover': {
        transform: 'scale(1.1)',
      },
    },
  },
  action: {
    textAlign: 'center',
    verticalAlign: 'middle',
    marginTop: 20,
    '& button': {
      margin: '0 3px',
      verticalAlign: 'middle',
    },
  },
  '@media(max-width: 850px)': {

  }
}));

const defaultTable = [
  [false, false, false, false, false, false, false, false],
  [false, false, false, false, false, false, false, false],
  [false, false, false, false, false, false, false, false],
];

let active = true;
let randTimeout = 0;
let styleTimeout = 0;

function WordNetwork(props) {
  const classes = useStyles();
  const [showTrans, setShowTrans] = useState([false, false, false, false]);
  const [words, setWords] = useState([]);
  const [word, setWord] = useState(false);
  const [table, setTable] = useState(defaultTable);
  const [finished, setFinished] = useState(false);
  const { exercise, startStats, finishExercise, action, type, close } = props.exerciseProps;
  // eslint-disable-next-line
  const wordsRefs = [0, 1, 2, 3].map(w => useRef());
  // eslint-disable-next-line
  const tableRefs = [0, 1, 2].map(_ => [0, 1, 2, 3, 4, 5, 6, 7].map(_ => useRef()));

  useEffect(() => {
    init();
    return () => {
      clearTimeout(randTimeout);
      clearTimeout(styleTimeout);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (word === false) return;
    // eslint-disable-next-line
  }, [word]);

  useEffect(() => {
    if (words.length === 0) return;
    randWord();
    // eslint-disable-next-line
  }, [words]);

  // const add = (colNo) => {
  //   action();
  //   if (getColLen(colNo) > 9) return;
  //   const newTable = [[ ...table[0]],[ ...table[1]],[ ...table[2]]];
  //   const idx = newTable[colNo].reduce((sum, current) => current ? sum + 1 : sum, 0);
  //   const newWords = [ ...words];
  //   const word = newWords.shift();
  //   if (word === undefined) return;
  //   newTable[colNo][idx] = word;
  //   setTable(newTable);
  //   setWords(newWords);
  // }

  const init = () => {
    startStats(type);
    let col = [0, 0, 0];
    const rawWords = [...exercise.words].map((word, idx) => {
      const group = Math.floor(idx / 8);
      const obj = { ...word, idx: col[group], group };
      col[group] = col[group] + 1;
      return obj;
    });
    setTable(defaultTable);
    setFinished(false);
    setWords(shuffle(rawWords));
  }

  // const getColLen = colNo => {
  //   return table[colNo].reduce((result, current) => current ? result + 1 : result, 0);
  // }

  const getText = (group, idx) => {
    const text = table[group][idx] ? table[group][idx] : '';
    return text;
  }

  const checkAnswer = (wordNo) => {
    action();
    if (!active) return;
    const correct = word.word.correct - 1 === wordNo;
    if (correct) {
      success(wordNo);
    } else {
      failure(wordNo);
    }
  }

  const success = (wordNo) => {
    const { group, idx } = words[word.no];
    const tRef = tableRefs[group][idx].current;
    const newWords = [...words];
    newWords.splice(word.no, 1);
    let text;
    switch (word.word.correct) {
      case 1:
        text = word.word.word1;
        break;
      case 2:
        text = word.word.word2;
        break;
      case 3:
        text = word.word.word3;
        break;
      case 4:
        text = word.word.word4;
        break;
      default:
        text = false;
    }
    const newTable = cloneObj(table);
    newTable[group][idx] = text;
    setTable(newTable);
    if (words.length === 1) {
      finish();
    } else {
      active = false;
      const ref = wordsRefs[wordNo].current;
      props.media.success();
      ref.classList.add('animated');
      ref.classList.add('pulse');
      styleTimeout = setTimeout(() => {
        ref.classList.remove('animated');
        ref.classList.remove('pulse');
        tRef.classList.remove('wn-active');
        active = true;
        setWords(newWords);
      }, 1000);
    }
  }

  const failure = (wordNo) => {
    const clArr = ['rubberBand', 'shake', 'swing', 'wobble', 'jello'];
    const cl = clArr[Math.floor(Math.random() * 5)];
    active = false;
    props.media.failure();
    const ref = wordsRefs[wordNo].current;
    ref.classList.add('animated');
    ref.classList.add(cl);
    styleTimeout = setTimeout(() => {
      ref.classList.remove('animated');
      ref.classList.remove(cl);
      active = true;
    }, 1000);
  }

  const finish = () => {
    props.media.finish();
    finishExercise();
    setFinished(true);
  }

  const randWord = () => {
    active = false;
    const len = words.length;
    let no = 0;
    let attempt = 0;
    const randLoop = () => {
      no = Math.floor(Math.random() * len);
      const { group, idx } = words[no];
      const ref = tableRefs[group][idx].current;
      ref.classList.add('wn-active');
      attempt++;
      randTimeout = setTimeout(() => {
        if (attempt < 9) {
          ref.classList.remove('wn-active');
          randLoop();
        } else {
          setWord({ no, word: words[no] });
          active = true;
        }
      }, 150);
    }
    if (len > 3) {
      randLoop();
    } else {
      const { group, idx } = words[0];
      const ref = tableRefs[group][idx].current;
      ref.classList.add('wn-active');
      setWord({ no: 0, word: words[0] });
      active = true;
    }
  }

  return (
    <div className={classes.wrapper}>
      <div>
        {!showTrans[3] && <h3 onClick={() => setShowTrans([false, false, false, true])}>{exercise.title}</h3>}
        {showTrans[3] && <h3 onClick={() => setShowTrans([false, false, false, false])}>{exercise.translation}</h3>}
        <p className={classes.help}>Wybierz słowo pasujące do wybranej kategorii</p>
        <div className={classes.availWords}>
          {word && <div>
            <span onClick={() => checkAnswer(0)} ref={wordsRefs[0]}>{word.word.word1}</span>
            <span onClick={() => checkAnswer(1)} ref={wordsRefs[1]}>{word.word.word2}</span>
            <span onClick={() => checkAnswer(2)} ref={wordsRefs[2]}>{word.word.word3}</span>
            <span onClick={() => checkAnswer(3)} ref={wordsRefs[3]}>{word.word.word4}</span>
          </div>}
        </div>
        <table className={classes.table}>
          <thead>
            <tr>
              {!showTrans[0] && <th onClick={() => setShowTrans([true, false, false])}><div>{exercise.group1}</div></th>}
              {showTrans[0] && <th onClick={() => setShowTrans([false, false, false])}><div>{exercise.translation1}</div></th>}
              {!showTrans[1] && <th onClick={() => setShowTrans([false, true, false])}><div>{exercise.group2}</div></th>}
              {showTrans[1] && <th onClick={() => setShowTrans([false, false, false])}><div>{exercise.translation2}</div></th>}
              {!showTrans[2] && <th onClick={() => setShowTrans([false, false, true])}><div>{exercise.group3}</div></th>}
              {showTrans[2] && <th onClick={() => setShowTrans([false, false, false])}><div>{exercise.translation3}</div></th>}
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2, 3, 4, 5, 6, 7].map(idx => <tr key={idx}>
              <td><div ref={tableRefs[0][idx]}>{getText(0, idx)}</div></td>
              <td><div ref={tableRefs[1][idx]}>{getText(1, idx)}</div></td>
              <td><div ref={tableRefs[2][idx]}>{getText(2, idx)}</div></td>
            </tr>)}
          </tbody>
        </table>
        <div className={classes.action}>
          {finished && <Button variant="outlined" color="primary" onClick={init}>Wyczyść</Button>}
          {finished && <Button variant="outlined" color="secondary" onClick={close}>Zamknij</Button>}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    media: state.media,
    mobile: state.mobile,
  }
}

function cloneObj(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function shuffle(array) {
  var currentIndex = array.length, temporaryValue, randomIndex;
  while (0 !== currentIndex) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;
    temporaryValue = array[currentIndex];
    array[currentIndex] = array[randomIndex];
    array[randomIndex] = temporaryValue;
  }
  return array;
}

export default connect(mapStateToProps)(WordNetwork);
